// Modal shown when the visitor lands with a QR ID that doesn't exist yet.
// On submit we create the object via the Makrolog backend proxy, then hard
// reload so the rest of the app reads the new metadata.

import React, { useState } from "react";
import {
    QrCode,
    X,
    Loader2,
    MapPin,
    Tag,
    Crosshair,
    HeartPulse,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";

export const QrCreateModal = ({ onClose }) => {
    const { t } = useI18n();
    const { qrId, createObject } = useQr();
    const [title, setTitle] = useState("");
    const [location, setLocation] = useState("");
    const [aed, setAed] = useState("");
    // Tracks whether the location field was populated via the GPS button.
    // We forward this to the backend so it becomes part of the object's
    // description.json ("locationGps": true/false).
    const [locationGps, setLocationGps] = useState(false);
    const [gpsBusy, setGpsBusy] = useState(false);
    const [gpsError, setGpsError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleLocationChange = (value) => {
        // If the user manually edits the field after using GPS, the value is
        // no longer GPS-derived – reset the flag.
        setLocation(value);
        if (locationGps) setLocationGps(false);
    };

    const reverseGeocode = async (lat, lng) => {
        try {
            const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
            const r = await fetch(url, {
                headers: { Accept: "application/json" },
            });
            if (!r.ok) throw new Error("nominatim");
            const data = await r.json();
            return data?.display_name || "";
        } catch {
            return "";
        }
    };

    const useGps = () => {
        setGpsError("");
        if (!("geolocation" in navigator)) {
            setGpsError(t("qr_create_gps_unsupported"));
            return;
        }
        setGpsBusy(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude, longitude } = pos.coords;
                const fallback = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
                const human = await reverseGeocode(latitude, longitude);
                setLocation(human || fallback);
                setLocationGps(true);
                setGpsBusy(false);
            },
            (err) => {
                setGpsBusy(false);
                if (err && err.code === 1) {
                    setGpsError(t("qr_create_gps_denied"));
                } else {
                    setGpsError(t("qr_create_gps_error"));
                }
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!title.trim()) {
            setError(t("qr_create_error_required"));
            return;
        }
        setSubmitting(true);
        try {
            await createObject({
                title: title.trim(),
                location: location.trim(),
                locationGps,
                aed: aed.trim(),
            });
            // Reload so the freshly created object shows up everywhere.
            window.location.reload();
        } catch (ex) {
            const detail = ex?.response?.data?.detail || ex?.message || "";
            setError(
                `${t("qr_create_error_failed")}${detail ? ` (${detail})` : ""}`,
            );
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="qr-create-overlay"
            className="fixed inset-0 z-[2100] flex items-stretch justify-center bg-black/80 backdrop-blur-sm overflow-y-auto"
        >
            <div
                data-testid="qr-create-modal"
                role="dialog"
                aria-modal="true"
                className="w-full min-h-full sm:min-h-0 sm:my-auto sm:max-w-lg bg-white sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
            >
                <div className="bg-[#0F8040] px-5 py-4 flex items-center justify-between gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center ring-2 ring-white/30">
                            <QrCode className="w-5 h-5 text-white" />
                        </div>
                        <div className="font-display font-black text-white text-lg sm:text-xl">
                            {t("qr_create_header")}
                        </div>
                    </div>
                    {onClose && (
                        <button
                            data-testid="qr-create-close"
                            onClick={onClose}
                            aria-label="Schließen"
                            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
                        >
                            <X className="w-5 h-5" strokeWidth={2.5} />
                        </button>
                    )}
                </div>

                <form
                    onSubmit={submit}
                    className="px-5 sm:px-8 pt-6 pb-8 space-y-4 flex-1 overflow-y-auto"
                >
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900 leading-tight">
                        {t("qr_create_title")}
                    </h2>
                    <p className="text-sm sm:text-[15px] text-zinc-600 leading-relaxed">
                        {t("qr_create_body")}
                    </p>
                    <div className="rounded-xl bg-zinc-100 px-3 py-2 text-xs font-mono text-zinc-500">
                        ID: <span className="text-zinc-900">{qrId}</span>
                    </div>

                    <label className="block">
                        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                            <Tag className="w-4 h-4 text-[#0F8040]" />
                            {t("qr_create_field_title")}
                        </span>
                        <input
                            data-testid="qr-create-title-input"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t("qr_create_field_title_placeholder")}
                            maxLength={200}
                            required
                            autoFocus
                            className="w-full h-12 px-4 rounded-xl bg-zinc-100 border-2 border-transparent focus-visible:border-[#0F8040] focus-visible:ring-0 focus-visible:outline-none font-display text-base text-zinc-900 placeholder:text-zinc-400"
                        />
                    </label>

                    <label className="block">
                        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                            <MapPin className="w-4 h-4 text-[#0F8040]" />
                            {t("qr_create_field_location")}
                            <span className="text-zinc-400 normal-case font-normal tracking-normal">
                                ({t("qr_create_field_optional")})
                            </span>
                        </span>
                        <div className="relative">
                            <input
                                data-testid="qr-create-location-input"
                                type="text"
                                value={location}
                                onChange={(e) =>
                                    handleLocationChange(e.target.value)
                                }
                                placeholder={t(
                                    "qr_create_field_location_placeholder",
                                )}
                                maxLength={400}
                                className="w-full h-12 pl-4 pr-14 rounded-xl bg-zinc-100 border-2 border-transparent focus-visible:border-[#0F8040] focus-visible:ring-0 focus-visible:outline-none font-display text-base text-zinc-900 placeholder:text-zinc-400"
                            />
                            <button
                                type="button"
                                data-testid="qr-create-gps-button"
                                onClick={useGps}
                                disabled={gpsBusy}
                                aria-label={t("qr_create_gps_button")}
                                title={t("qr_create_gps_button")}
                                className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center transition ${
                                    locationGps
                                        ? "bg-[#0F8040] text-white hover:bg-[#0d6f37]"
                                        : "bg-zinc-200 hover:bg-zinc-300 text-zinc-700"
                                } disabled:opacity-60`}
                            >
                                {gpsBusy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Crosshair
                                        className="w-4 h-4"
                                        strokeWidth={2.5}
                                    />
                                )}
                            </button>
                        </div>
                        {gpsError && (
                            <div
                                data-testid="qr-create-gps-error"
                                className="mt-1.5 text-xs text-[#A72920] font-display font-semibold"
                            >
                                {gpsError}
                            </div>
                        )}
                        {locationGps && !gpsError && (
                            <div
                                data-testid="qr-create-gps-used"
                                className="mt-1.5 text-xs text-[#0F8040] font-display font-semibold"
                            >
                                {t("qr_create_gps_used")}
                            </div>
                        )}
                    </label>

                    <label className="block">
                        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                            <HeartPulse className="w-4 h-4 text-[#0F8040]" />
                            {t("qr_create_field_aed")}
                            <span className="text-zinc-400 normal-case font-normal tracking-normal">
                                ({t("qr_create_field_optional")})
                            </span>
                        </span>
                        <input
                            data-testid="qr-create-aed-input"
                            type="text"
                            value={aed}
                            onChange={(e) => setAed(e.target.value)}
                            placeholder={t("qr_create_field_aed_placeholder")}
                            maxLength={400}
                            className="w-full h-12 px-4 rounded-xl bg-zinc-100 border-2 border-transparent focus-visible:border-[#0F8040] focus-visible:ring-0 focus-visible:outline-none font-display text-base text-zinc-900 placeholder:text-zinc-400"
                        />
                        <span className="block mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
                            {t("qr_create_field_aed_help")}
                        </span>
                    </label>

                    {error && (
                        <div
                            data-testid="qr-create-error"
                            className="text-sm text-[#A72920] font-display font-semibold bg-[#A72920]/5 rounded-lg px-3 py-2"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        data-testid="qr-create-submit"
                        type="submit"
                        disabled={submitting}
                        className="w-full h-12 rounded-xl bg-[#0F8040] hover:bg-[#0d6f37] disabled:opacity-60 text-white font-display font-black text-base flex items-center justify-center gap-2 transition"
                    >
                        {submitting ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            t("qr_create_submit")
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};
